import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@/types/userRole';
import { logger } from '@/utils/logger';

export const useUsers = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, email, first_name, last_name, role')
        .order('created_at', { ascending: false });
      if (error) throw error;

      const { data: roles } = await supabase.from('user_roles').select('user_id, role');
      const roleMap = new Map<string, string>();
      (roles ?? []).forEach((r: any) => roleMap.set(r.user_id, r.role));

      const mapped: User[] = (profiles ?? []).map((p: any) => ({
        id: p.id,
        name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email,
        email: p.email,
        role: (roleMap.get(p.id) ?? p.role ?? 'user') as any,
      }));
      setUsers(mapped);
    } catch (error) {
      logger.error('Error loading users:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const inviteUser = async (data: { email: string; role: string; name: string }) => {
    // Invitations require an admin edge function to create auth users.
    // Consumers should call the appropriate admin endpoint; kept as a no-op refresh here.
    logger.info('inviteUser requested', data);
    await loadUsers();
  };

  const removeUser = async (userId: string) => {
    const { error } = await supabase.from('user_roles').delete().eq('user_id', userId);
    if (error) logger.error('removeUser role delete failed', error);
    setUsers((prev) => prev.filter((u) => u.id !== userId));
  };

  return { users, isLoading, inviteUser, removeUser };
};
